import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import AuthModal from '../components/auth/AuthModal';

interface AuthModalContextType {
  openLogin: () => void;
  openRegister: () => void;
  closeModal: () => void;
  isOpen: boolean;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

interface AuthModalProviderProps {
  children: ReactNode;
}

export const AuthModalProvider: React.FC<AuthModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  // Controls whether the <AuthModal> component is mounted in the tree.
  // We keep it mounted briefly after isOpen turns false so the Dialog
  // can play its exit animation, then fully unmount it so no invisible
  // portal / backdrop can linger in the DOM.
  const [mounted, setMounted] = useState(false);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Opening: mount immediately, cancel any pending unmount
      if (unmountTimer.current) clearTimeout(unmountTimer.current);
      setMounted(true);
    } else if (mounted) {
      // Closing: give MUI Dialog 350ms for its exit transition,
      // then fully unmount the component tree + portal.
      unmountTimer.current = setTimeout(() => {
        setMounted(false);
        // Clean up any body styles MUI may have left behind
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        document.body.classList.remove('MuiModal-open');
      }, 350);
    }
    return () => {
      if (unmountTimer.current) clearTimeout(unmountTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // intentionally omit `mounted` to avoid loops

  const openLogin = useCallback(() => {
    setMode('login');
    setIsOpen(true);
  }, []);

  const openRegister = useCallback(() => {
    setMode('register');
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <AuthModalContext.Provider value={{ openLogin, openRegister, closeModal, isOpen }}>
      {children}
      {mounted && (
        <AuthModal
          open={isOpen}
          onClose={closeModal}
          initialMode={mode}
        />
      )}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = (): AuthModalContextType => {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
};

export default useAuthModal;
